import React, { useCallback, useState } from 'react';
import { Popover } from '@mui/material';
import { WarningRounded as WarningRoundedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import GenerateScriptIcon from '@src/assets/icons/generating-script.svg';

import {
  StyledWarningButton,
  StyledPopoverContent,
  StyledPopoverHeader,
  StyledPopoverBody,
} from './index.style';

const NoteChangedWarningPopover = ({
  blockId,
  onOpenAIGenerateDialog,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleGenerateClick = useCallback(() => {
    if (disabled) return;
    handleClose();
    onOpenAIGenerateDialog?.(blockId);
  }, [disabled, handleClose, onOpenAIGenerateDialog, blockId]);

  const open = Boolean(anchorEl);

  return (
    <div onMouseLeave={handleClose}>
      <StyledWarningButton
        type="button"
        onMouseEnter={handleOpen}
        aria-label={t('slideNoteChangedTitle')}
      >
        <WarningRoundedIcon
          sx={{ color: COLOR.textWarning, fontSize: '24px' }}
        />
      </StyledWarningButton>
      {open && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          disableRestoreFocus
          sx={{ pointerEvents: 'none' }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
              width: '320px',
              pointerEvents: 'auto',
            },
          }}
        >
          <StyledPopoverContent>
            <StyledPopoverHeader>
              <Typography variant="body-16-medium" color={COLOR.darkGray}>
                {t('slideNoteChangedTitle')}
              </Typography>
            </StyledPopoverHeader>
            <StyledPopoverBody>
              <Typography variant="body-14-regular" color={COLOR.darkGray}>
                {t('slideNoteChangedDescription')}
              </Typography>
              <CommonButton
                variant="outlined"
                disabled={disabled}
                startIcon={<img src={GenerateScriptIcon} alt="" />}
                onClick={handleGenerateClick}
                customStyle={{
                  width: '100%',
                  height: '40px',
                  borderRadius: '8px',
                  border: `1px solid ${COLOR.indigo[16]}`,
                  backgroundColor: COLOR.white,
                  padding: '8px 16px',
                  textTransform: 'none',
                  '&:hover': {
                    border: `1px solid ${COLOR.indigo[16]}`,
                    backgroundColor: COLOR.white,
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: '8px',
                    marginLeft: 0,
                  },
                }}
                textProps={{
                  variantText: 'body-14-semibold',
                  color: COLOR.darkBlue,
                }}
              >
                {t('aiPresentationScript')}
              </CommonButton>
            </StyledPopoverBody>
          </StyledPopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default NoteChangedWarningPopover;
